import React from 'react';
import styled, { css } from 'styled-components';
import { FontWeightSemiBold } from 'autogen/design-tokens/tokens';
import type { avatarSizeKeys, avatarSizesObject, IAvatar } from './shared/types';
import sizeStyling, { avatarSizes, badgeSize, type StyleSizeType } from './sizeStyling';

type Test = Pick<IAvatar, 'backgroundColor' | 'size'> & { styleSizeObject: StyleSizeType };

const Wrapper = styled.div<Test>`
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 100%;
    ${sizeStyling}

    ${({ backgroundColor }) =>
        backgroundColor &&
        css`
            background-color: ${backgroundColor};
        `}
`;

const Initials = styled.span`
    line-height: 0;
    font-weight: ${FontWeightSemiBold};
`;

const Badge = styled.div<{
    backgroundColor?: string;
    size: avatarSizesObject | avatarSizeKeys;
    styleSizeObject: StyleSizeType;
}>`
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 100%;
    overflow: hidden;
    ${sizeStyling}

    ${({ backgroundColor }) =>
        backgroundColor &&
        css`
            background-color: ${backgroundColor};
        `}
`;

const ImageContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    overflow: hidden;
    border-radius: 100%;
`;

const Image = styled.img`
    height: 100%;
    object-fit: cover;
`;

function handleInitials(string: string): string {
    const initialsArray = string.split(' ').map((subString) => subString[0]);
    initialsArray.splice(1, initialsArray.length - 2);

    return initialsArray.join('').toUpperCase();
}

export default function Avatar(props: IAvatar): JSX.Element {
    const { avatar, backgroundColor, badge, size = 'md', ...restProps } = props;
    const { icon, color } = badge || {};

    return (
        <Wrapper size={size} styleSizeObject={avatarSizes} backgroundColor={backgroundColor} {...restProps}>
            {typeof avatar === 'string' ? (
                <Initials>{handleInitials(avatar)}</Initials>
            ) : (
                <ImageContainer>
                    <Image src={avatar.url} width={avatar.width} height={avatar.height} alt="" />
                </ImageContainer>
            )}
            {badge && (
                <Badge backgroundColor={color} size={size} styleSizeObject={badgeSize}>
                    {icon}
                </Badge>
            )}
        </Wrapper>
    );
}
